import { inject, Injectable, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, of, BehaviorSubject } from 'rxjs';
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  sub: string;
  name: string;
  iat: number;
  exp: number;
  apikey: string;
  [key: string]: unknown;
  organization: string;
  role?: 'agent' | 'admin' | 'platform';
}

@Injectable({
  providedIn: 'root'
})
export class JwtService {
  private readonly VERIFY_TOKEN_URL = `${environment.apiUrl}/auth/login`;

  private tokenSubject = new BehaviorSubject<DecodedToken | null>(null);
  public token$ = this.tokenSubject.asObservable();

  private hasCheckedUrl = false;

  private http = inject(HttpClient);
  private router = inject(Router);

  constructor() {
    this.initializeToken();
  }

  private loadDevToken(): string | null {
    if (!environment.production) {
      const devToken = (environment as Record<string, unknown>)['devJwtToken'] as string | undefined;
      if (devToken && devToken.length > 0 && devToken !== 'your-jwt-token-here') {
        return devToken;
      }
    }
    return null;
  }

  private initializeToken(): void {
    const tokenFromUrl = this.getTokenFromUrl();
    if (tokenFromUrl) {
      this.setToken(tokenFromUrl);
      this.clearTokenFromUrl();
      this.hasCheckedUrl = true;
      return;
    }

    const decodedToken = this.decodeToken();
    if (decodedToken) {
      this.tokenSubject.next(decodedToken);
      return;
    }

    const devToken = this.loadDevToken();
    if (devToken) {
      this.setToken(devToken);
      return;
    }

    this.tokenSubject.next(null);
  }

  public verifyTokenWithApi(): Observable<boolean> {
    const token = this.getToken();
    if (!token) return of(false);

    return this.http.post<{ success: boolean }>(this.VERIFY_TOKEN_URL, { token }, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).pipe(
      map(response => response.success === true),
      catchError(() => of(false))
    );
  }

  public getToken(): string | null {
    return sessionStorage.getItem('gestion_reservations_bookings_SESSION');
  }

  public setToken(token: string): void {
    if (!this.isValidJwtFormat(token)) {
      if (isDevMode()) console.warn('[JwtService] Invalid JWT format');
      return;
    }
    sessionStorage.setItem('gestion_reservations_bookings_SESSION', token);
    const decodedToken = this.decodeToken();
    this.tokenSubject.next(decodedToken);
  }

  private isValidJwtFormat(token: string): boolean {
    if (!token || typeof token !== 'string') return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    // Accept both base64url (RFC 7515) and standard base64 (with +, /, =).
    // The signature segment can be empty for unsigned JWTs (`alg: none`).
    const base64AnyRegex = /^[A-Za-z0-9_+/=-]*$/;
    return parts[0].length > 0 && parts[1].length > 0 && parts.every(part => base64AnyRegex.test(part));
  }

  public removeToken(redirectToLogin: boolean = false): void {
    sessionStorage.removeItem('gestion_reservations_bookings_SESSION');
    this.tokenSubject.next(null);
    if (redirectToLogin) this.redirectLoginPage();
  }

  public checkTokenAndRedirect(): Observable<boolean> {
    const existingToken = this.getToken();
    if (!existingToken && !this.hasCheckedUrl) {
      const tokenFromUrl = this.getTokenFromUrl();
      if (tokenFromUrl) {
        this.setToken(tokenFromUrl);
        this.clearTokenFromUrl();
      }
      this.hasCheckedUrl = true;
    }

    return this.verifyTokenWithApi().pipe(
      map(isValid => {
        if (!isValid) {
          this.removeToken();
          this.hasCheckedUrl = false;
          return false;
        }
        return true;
      }),
      catchError(() => {
        this.removeToken();
        this.hasCheckedUrl = false;
        return of(false);
      })
    );
  }

  public redirectLoginPage(): void {
    this.router.navigate(['/unauthorized']);
  }

  public getTokenFromUrl(): string | null {
    const hash = window.location.hash;
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const tokenFromHash = hashParams.get('token') || hashParams.get('access_token');
      if (tokenFromHash) return tokenFromHash;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromQuery = urlParams.get('token');
    if (tokenFromQuery) return tokenFromQuery;

    return null;
  }

  private clearTokenFromUrl(): void {
    const url = new URL(window.location.href);
    let urlChanged = false;

    if (url.searchParams.has('token')) {
      url.searchParams.delete('token');
      urlChanged = true;
    }

    if (url.hash) {
      const hashParams = new URLSearchParams(url.hash.substring(1));
      if (hashParams.has('token') || hashParams.has('access_token')) {
        hashParams.delete('token');
        hashParams.delete('access_token');
        const newHash = hashParams.toString();
        url.hash = newHash ? '#' + newHash : '';
        urlChanged = true;
      }
    }

    if (urlChanged) {
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  public decodeToken(): DecodedToken | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = jwtDecode<DecodedToken>(token);
      if (!decoded.sub || !decoded.exp) return null;
      return decoded;
    } catch {
      return null;
    }
  }

  public isTokenExpired(): boolean {
    const decoded = this.decodeToken();
    if (!decoded) return true;
    return decoded.exp < Math.floor(Date.now() / 1000);
  }

  public isSuperAdmin(): boolean {
    const decoded = this.decodeToken();
    return !!decoded?.sub && /@speedpresta\.com$/i.test(decoded.sub);
  }

  public readonly isSuperAdmin$: Observable<boolean> = this.token$.pipe(
    map(t => !!t?.sub && /@speedpresta\.com$/i.test(String(t.sub))),
  );

  public isAdmin(): boolean {
    const decoded = this.decodeToken();
    return decoded?.role === 'admin' || decoded?.role === 'platform';
  }

  public isAgent(): boolean {
    const decoded = this.decodeToken();
    return !!decoded?.sub && !this.isAdmin();
  }

  public readonly isAdmin$: Observable<boolean> = this.token$.pipe(
    map(t => {
      const role = (t as any)?.role;
      return role === 'admin' || role === 'platform';
    }),
  );

  public readonly isAgent$: Observable<boolean> = this.token$.pipe(
    map(t => !!t?.sub && (t as any)?.role !== 'admin' && (t as any)?.role !== 'platform'),
  );

  public updateTokenObservable(): void {
    this.tokenSubject.next(this.decodeToken());
  }
}
